import React, { useEffect, useMemo } from "react";
import { connect, ConnectedProps } from "react-redux";
import { AppDispatch, RootState } from "store";
import { FilterPolygons } from "./FilterPolygons";
import { regionSelector, actions as regionsActions } from "reducers/regions";
import { sessionSelector, actions as sessionActions } from "reducers/session";
import { actions as notificationActions } from "reducers/notification";
import { PolygonFiltersType } from "reducers/types/regions";
import { FilterPolygonContainerType } from "./types";
import { RegionType } from "sagas/types/regions";
import { NotificationStateType } from "reducers/types/notification";
import { useTranslation } from "react-i18next";
import { dirtyAlert } from "utils/alerts";
import useBasicData from "hooks/useBasicData";

const FilterPolygonsContainer: React.FC<
  FilterPolygonContainerType & ConnectorProps
> = (props) => {
  const { t } = useTranslation();
  const { regions } = useBasicData(["regions"]);
  const initialValues = useMemo(() => {
    return { ...props.filters };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filters.region, props.actualPage]);

  const handleSubmit = (region: RegionType) => {
    if (region) {
      const callback = () => {
        props.setFilters({ region, updated: false });
        props.setDirty(false);
        if (props.actualPage === "general") {
          props.getRegionDetail(region.id);
          props.getNextRain(region.id);
        } else props.getPricingDetail(region.id);
      };

      if (props.generalFormDirty) showDirtyNotification(callback);
      else callback();
    }
  };

  const regionChanged = (region?: RegionType) => {
    const callback = () => {
      props.setFilters({ region: region || null, updated: true });
      props.setDirty(false);
    };
    if (props.generalFormDirty) showDirtyNotification(callback);
    else if (!props.filters.updated) props.setFilterUpdated(true);
  };

  const showDirtyNotification = (onClose: () => void) => {
    return props.showNotification(
      dirtyAlert({
        onCancel: onClose,
        message: t("alerts:changesWillBeLostMessage"),
      })
    );
  };

  useEffect(() => {
    props.setFilterUpdated(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.actualPage]);

  return (
    <FilterPolygons
      {...props}
      regions={regions}
      handleSubmit={handleSubmit}
      initialValues={initialValues}
      regionChanged={regionChanged}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  generalFormDirty: sessionSelector(state).dirtyForm,
  actualPage: regionSelector(state).actualPage,
  filters: regionSelector(state).filters,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setDirty: (dirty: boolean) => {
    dispatch(sessionActions.setDirty(dirty));
  },
  setFilters: (params: PolygonFiltersType) => {
    dispatch(regionsActions.setFilters(params));
  },
  getRegionDetail: (regionId: string) => {
    dispatch(regionsActions.getRegionDetailRequest(regionId));
  },
  getPricingDetail: (params: string) => {
    dispatch(regionsActions.getPricingDetailRequest(params));
  },
  setFilterUpdated: (updated: boolean) => {
    dispatch(regionsActions.setFilterUpdated(updated));
  },
  showNotification: (content: NotificationStateType) => {
    dispatch(notificationActions.showNotification(content));
  },
  getNextRain: (regionId: string) => {
    dispatch(regionsActions.getNextRainRequest(regionId));
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ConnectorProps = ConnectedProps<typeof connector>;

export default connector(FilterPolygonsContainer);
